import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { config } from "../../config/config";
import HeroSectionTab from "../../components/admin/HeroSectionTab";
import mediaService from "../../services/mediaService";

function HomepageManagement() {
  const [activeTab, setActiveTab] = useState("hero");
  const [loading, setLoading] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Homepage Management
        </h1>
        <p className="text-gray-600">
          Customize your website homepage content and layout
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("hero")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "hero"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Hero Section
          </button>
          <button
            onClick={() => setActiveTab("programs")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "programs"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Featured Programs
          </button>
          <button
            onClick={() => setActiveTab("services")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "services"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab("about")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "about"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            About Section
          </button>
          <button
            onClick={() => setActiveTab("sections")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "sections"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Section Headings
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      <div className="mt-6">
        {activeTab === "hero" && <HeroSectionTab />}
        {activeTab === "programs" && <FeaturedProgramsTab />}
        {activeTab === "services" && <ServicesTab />}
        {activeTab === "about" && <AboutSectionTab />}
        {activeTab === "sections" && <SectionsTab />}
      </div>
    </div>
  );
}

// Featured Programs Component
function FeaturedProgramsTab() {
  const [programs, setPrograms] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      const response = await fetch(`${config.api.baseURL}/programs/`);
      const data = await response.json();
      setPrograms(data);

      // Get currently featured programs
      const featured = data.filter((p) => p.featured);
      setSelectedPrograms(featured.map((p) => p.id));
    } catch (error) {
      console.error("Error loading programs:", error);
      // Fallback data
      setPrograms([
        { id: 1, title: "From Idea to Funded Venture", featured: true },
        { id: 2, title: "Leading with Impact", featured: true },
        { id: 3, title: "Strategic Decision-Making", featured: true },
        { id: 4, title: "Growth Marketing Mastery", featured: false },
        { id: 5, title: "Career Acceleration Program", featured: false },
      ]);
      setSelectedPrograms([1, 2, 3]);
    } finally {
      setLoading(false);
    }
  };

  const toggleProgramSelection = (programId) => {
    setSelectedPrograms((prev) => {
      if (prev.includes(programId)) {
        return prev.filter((id) => id !== programId);
      } else {
        if (prev.length >= 3) {
          alert("You can only select up to 3 featured programs");
          return prev;
        }
        return [...prev, programId];
      }
    });
  };

  const saveFeaturedPrograms = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${config.api.baseURL}/homepage/featured-programs`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selectedPrograms),
        },
      );

      if (response.ok) {
        alert("Featured programs updated successfully!");
        // Update local state
        setPrograms(
          programs.map((p) => ({
            ...p,
            featured: selectedPrograms.includes(p.id),
          })),
        );
      } else {
        throw new Error("Failed to update featured programs");
      }
    } catch (error) {
      console.error("Error updating featured programs:", error);
      alert("Failed to update featured programs. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading programs...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Manage Featured Programs</h2>
          <div className="text-sm text-gray-600">
            Selected: {selectedPrograms.length}/3
          </div>
        </div>

        <p className="text-gray-600 mb-6">
          Select up to 3 programs to feature on the homepage. Drag to reorder.
        </p>

        {/* Selected Programs */}
        <div className="mb-8">
          <h3 className="font-medium mb-4">
            Selected Programs (Featured on Homepage)
          </h3>
          <div className="space-y-3">
            {selectedPrograms.map((programId, index) => {
              const program = programs.find((p) => p.id === programId);
              if (!program) return null;

              return (
                <div
                  key={programId}
                  className="flex items-center justify-between p-4 bg-blue-50 rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{program.title}</div>
                      <div className="text-sm text-gray-500">
                        Will appear as featured program #{index + 1}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleProgramSelection(programId)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              );
            })}

            {selectedPrograms.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No programs selected. Select programs below to feature them on
                the homepage.
              </div>
            )}
          </div>
        </div>

        {/* Available Programs */}
        <div>
          <h3 className="font-medium mb-4">Available Programs</h3>
          <div className="space-y-3">
            {programs
              .filter((p) => !selectedPrograms.includes(p.id))
              .map((program) => (
                <div
                  key={program.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="font-medium">{program.title}</div>
                  <button
                    onClick={() => toggleProgramSelection(program.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add to Featured
                  </button>
                </div>
              ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t">
          <button
            onClick={saveFeaturedPrograms}
            disabled={saving || selectedPrograms.length === 0}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Featured Programs"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            {selectedPrograms.length === 0 &&
              "Select at least one program to save"}
          </p>
        </div>
      </div>
    </div>
  );
}

// Services Component
function ServicesTab() {
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    cta_text: "",
    cta_href: "",
    icon: "📦",
    service_order: 0,
  });

  const icons = [
    "🏢",
    "💼",
    "👨‍🏫",
    "🎓",
    "💡",
    "🚀",
    "📈",
    "🎯",
    "🤝",
    "🌟",
    "⚡",
    "📊",
  ];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/homepage/services`,
      );
      const data = await response.json();
      // Ensure data is an array
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingService) {
        // Update existing service
        const response = await fetch(
          `${config.api.baseURL}/admin/homepage/services/${editingService.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          },
        );

        if (response.ok) {
          alert("Service updated successfully!");
          setServices(
            services.map((s) =>
              s.id === editingService.id ? { ...s, ...formData } : s,
            ),
          );
        }
      } else {
        // Create new service
        const response = await fetch(
          `${config.api.baseURL}/admin/homepage/services`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          },
        );

        const result = await response.json();
        if (response.ok) {
          alert("Service created successfully!");
          setServices([...services, { id: result.id, ...formData }]);
        }
      }

      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error("Error saving service:", error);
      alert("Failed to save service. Please try again.");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      cta_text: service.cta_text,
      cta_href: service.cta_href || "",
      icon: service.icon,
      service_order: service.service_order,
    });
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) {
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/homepage/services/${serviceId}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        alert("Service deleted successfully!");
        setServices(services.filter((s) => s.id !== serviceId));
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Failed to delete service. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      cta_text: "",
      cta_href: "",
      icon: "📦",
      service_order: 0,
    });
    setEditingService(null);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Manage Services</h2>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Add New Service
          </button>
        </div>

        {/* Services List */}
        <div className="space-y-4 mb-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center">
                <div className="text-2xl mr-4">{service.icon}</div>
                <div>
                  <div className="font-medium">{service.title}</div>
                  <div className="text-sm text-gray-500">
                    {service.description}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    CTA: {service.cta_text}
                    {service.cta_href ? ` (${service.cta_href})` : ""} | Order:{" "}
                    {service.service_order}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(service)}
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded text-sm hover:bg-blue-100"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(service.id)}
                  className="px-3 py-1 bg-red-50 text-red-600 rounded text-sm hover:bg-red-100"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Service Form */}
        {showForm && (
          <div className="border-t pt-6 mt-6">
            <h3 className="font-semibold mb-4">
              {editingService ? "Edit Service" : "Add New Service"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CTA Text
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) =>
                      setFormData({ ...formData, cta_text: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CTA Link (URL or path)
                  </label>
                  <input
                    type="text"
                    value={formData.cta_href}
                    onChange={(e) =>
                      setFormData({ ...formData, cta_href: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="/consulting or https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.service_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        service_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <div className="flex flex-wrap gap-2">
                  {icons.map((icon) => (
                    <button
                      type="button"
                      key={icon}
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`text-2xl p-2 rounded ${
                        formData.icon === icon
                          ? "bg-blue-100 ring-2 ring-blue-500"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  {editingService ? "Update Service" : "Create Service"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// About Section Component
function AboutSectionTab() {
  const [aboutData, setAboutData] = useState({
    title: "",
    content: "",
    founder_story: "",
    founder_image: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      const response = await fetch(`${config.api.baseURL}/admin/homepage/`);
      if (!response.ok) {
        throw new Error("Failed to load homepage data");
      }
      const data = await response.json();
      setAboutData({
        title: data?.about_title || "About Innobig",
        content: data?.about_content || "",
        founder_story: data?.founder_story || "",
        founder_image: data?.founder_image || "",
      });
    } catch (error) {
      console.error("Error loading about data:", error);
      setAboutData({
        title: "About Innobig",
        content:
          "A professional development hub founded by Mohammad Abdul Matin Emon, dedicated to empowering growth through innovation and leadership.",
        founder_story:
          "Former Doctorola & Praava Health executive turned entrepreneur, sharing lessons from failures and successes.",
        founder_image: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMediaLibrary = async () => {
    setLoadingMedia(true);
    try {
      const media = await mediaService.getMediaList("general");
      setMediaLibrary(Array.isArray(media) ? media : []);
      setShowMediaLibrary(true);
    } catch (error) {
      console.error("Error loading media library:", error);
      alert("Failed to load media library");
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleSelectImage = (media) => {
    const selectedPath = media.path || media.url || media.file_path || "";
    const fullUrl = mediaService.getFileUrl(selectedPath);
    setAboutData((prev) => ({
      ...prev,
      founder_image: selectedPath.startsWith("http") ? selectedPath : fullUrl,
    }));
    setShowMediaLibrary(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/homepage/about`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: aboutData.title,
            content: aboutData.content,
            founder_story: aboutData.founder_story,
            founder_image: aboutData.founder_image,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update about section");
      }

      alert("About section updated successfully!");
    } catch (error) {
      console.error("Error updating about section:", error);
      alert("Failed to update about section. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading about section...</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-3xl">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-6">Edit About Section</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title
              </label>
              <input
                type="text"
                value={aboutData.title}
                onChange={(e) =>
                  setAboutData({ ...aboutData, title: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                About Content
              </label>
              <textarea
                value={aboutData.content}
                onChange={(e) =>
                  setAboutData({ ...aboutData, content: e.target.value })
                }
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Founder Story
              </label>
              <textarea
                value={aboutData.founder_story}
                onChange={(e) =>
                  setAboutData({ ...aboutData, founder_story: e.target.value })
                }
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Founder Image
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={aboutData.founder_image}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      founder_image: e.target.value,
                    })
                  }
                  placeholder="Paste image URL or choose from media"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                />
                <button
                  type="button"
                  onClick={loadMediaLibrary}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loadingMedia}
                >
                  {loadingMedia ? "Loading..." : "Choose"}
                </button>
              </div>
              {aboutData.founder_image && (
                <div className="mt-3">
                  <img
                    src={
                      aboutData.founder_image.startsWith("http")
                        ? aboutData.founder_image
                        : mediaService.getFileUrl(aboutData.founder_image)
                    }
                    alt="Founder"
                    className="w-40 h-40 object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>

            <div className="pt-6 border-t">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="font-semibold">Select Image</h3>
              <button
                onClick={() => setShowMediaLibrary(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              {mediaLibrary.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No media files found
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {mediaLibrary.map((media, index) => {
                    const fullUrl = mediaService.getFileUrl(
                      media.path || media.url || media.file_path,
                    );
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectImage(media)}
                        className="relative group"
                      >
                        <img
                          src={fullUrl}
                          alt={media.name || media.filename}
                          className="w-full aspect-square object-cover rounded-lg border-2 border-transparent group-hover:border-blue-500 transition"
                        />
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {media.name || media.filename}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Section Headings Component
function SectionsTab() {
  const [formData, setFormData] = useState({
    programs_title: "Flagship Programs",
    programs_subtitle: "",
    services_title: "Our Services",
    services_subtitle: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/homepage/sections`,
      );
      if (!response.ok) {
        throw new Error("Failed to load section headings");
      }
      const data = await response.json();
      setFormData({
        programs_title: data?.programs_title || "Flagship Programs",
        programs_subtitle: data?.programs_subtitle || "",
        services_title: data?.services_title || "Our Services",
        services_subtitle: data?.services_subtitle || "",
      });
    } catch (error) {
      console.error("Error loading section headings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/homepage/sections`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update section headings");
      }

      alert("Section headings updated successfully!");
    } catch (error) {
      console.error("Error updating section headings:", error);
      alert("Failed to update section headings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading section headings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-6">Section Headings</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Featured Programs</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                name="programs_title"
                value={formData.programs_title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle
              </label>
              <textarea
                name="programs_subtitle"
                value={formData.programs_subtitle}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Our Services</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                name="services_title"
                value={formData.services_title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle
              </label>
              <textarea
                name="services_subtitle"
                value={formData.services_subtitle}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HomepageManagement;
